import "./Ingram.scss";

import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  useTransform
} from "framer-motion";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import React, { MutableRefObject, useRef, useState } from "react";

import Bottle from "../../Assets/Emojis/Bottle.png";
import PartyFace from "../../Assets/Emojis/Party Face.png";
import Party from "../../Assets/Emojis/Party.png";
import Sparkles from "../../Assets/Emojis/Sparkles.png";
import Confetti from "../../Assets/Lotties/confetti.json";
import Azure_Functions from "../../Assets/reducedLogos/Azure Functions Integrations Logo-1600.png";
import Azure from "../../Assets/reducedLogos/Azure.png";
import Google_Drive from "../../Assets/reducedLogos/Google Drive Integrations Logo-1600.png";
import Jotform from "../../Assets/reducedLogos/Jotform.png";
import Microsoft_Excel from "../../Assets/reducedLogos/Microsoft Excel Integrations Logo-1600.png";
import Microsoft_Teams from "../../Assets/reducedLogos/Microsoft Teams Integrations Logo-1600.png";
import Microsoft_Word from "../../Assets/reducedLogos/Microsoft Word Integrations Logo-1600.png";
import OneDrive from "../../Assets/reducedLogos/OneDrive.png";
import Outlook from "../../Assets/reducedLogos/Outlook.png";
import PDF from "../../Assets/reducedLogos/pdf.png";
import Power from "../../Assets/reducedLogos/Power Automate.png";
import SASS from "../../Assets/reducedLogos/SASS.png";
import Sharepoint from "../../Assets/reducedLogos/SharePoint.png";
import VSCode from "../../Assets/reducedLogos/VS Code.png";
import Zapier from "../../Assets/reducedLogos/Zapier.png";
import Text from "../../Obelisk/Components/Text/Text";
import { ContentPadding } from "../../Obelisk/Structure/Padding";

interface TransformMap {
  value: any;
  transform: any;
}

const DEFAULT_THRESHOLD = 0.3;
const DEFAULT_STEP_COUNT = 6;
const FIRST_OFFSET = 0.02;
const QUICK_MOVE = 0.01;
const ONE_TICK = 0.002;

const getCardScrollThresholds = (
  steps = DEFAULT_STEP_COUNT,
  threshold = DEFAULT_THRESHOLD
) => {
  const step = 1 / steps;
  const result: TransformMap[] = [];
  let cur = 0;
  while (cur < steps) {
    result.push({
      value: cur * step + FIRST_OFFSET,
      transform: `${-1 * cur}00%`
    });
    result.push({
      value: cur * step + step * threshold + FIRST_OFFSET,
      transform: `${-1 * cur}00%`
    });
    cur++;
  }
  return result;
};

const getCircleScrollThresholds = (
  steps = DEFAULT_STEP_COUNT,
  threshold = DEFAULT_THRESHOLD
) => {
  const step = 1 / steps;
  const motionPart = step * (1 - threshold);
  const result: TransformMap[] = [];
  let currentStep = 0;

  const createKeyframe = (
    value: number,
    y: number,
    x: number,
    scale: number,
    background: string
  ) => ({
    value,
    transform: { y, x, scale, background }
  });

  while (currentStep < steps - 1) {
    const motionStart = step * (currentStep + threshold);

    result.push(createKeyframe(currentStep * step, 0, 0, 1, "#282a36f0"));
    result.push(createKeyframe(motionStart - QUICK_MOVE, 0, 0, 1, "#282a36f0"));
    result.push(createKeyframe(motionStart - ONE_TICK, 0, 0, 0, "#282a36f0"));
    result.push(createKeyframe(motionStart, 0, 0, 0, "#282a36f0"));
    result.push(createKeyframe(motionStart + ONE_TICK, 300, 300, 0, "#574ae2"));
    result.push(
      createKeyframe(motionStart + QUICK_MOVE, 300, 300, 5, "#574ae2")
    );
    result.push(
      createKeyframe(motionStart + motionPart / 2, 100, 100, 4.5, "#574ae2d0")
    );
    result.push(
      createKeyframe(motionStart + motionPart - ONE_TICK, 0, 0, 1, "#282a36f0")
    );

    currentStep++;
  }

  return result;
};

const getScrollPercentage = (scrollY: number) => {
  const start = document.getElementById("ing-stickyStart");
  const sticky = document.getElementById("ing-sticky");
  const spacer = document.getElementById("ing-spacer");
  if (start && sticky && spacer) {
    const topOffset = Math.max(0, scrollY - start.offsetTop);
    const totalHeight =
      sticky.clientHeight + spacer.clientHeight - window.innerHeight;
    return topOffset / totalHeight;
  }
  return 0;
};

// MAKE UNIVERSAL COMPONENT
// List of children get rendered as cards
// Length calculated from list
export const Ingram = React.forwardRef((props, ref) => {
  const [cardIndex, setCardIndex] = useState(0);
  const [triggerConfetti, setTriggerConfetti] = useState(true);
  const progress = useMotionValue(0);
  const confettiRef: MutableRefObject<LottieRefCurrentProps | null> =
    useRef(null);

  const { scrollY } = useScroll({
    layoutEffect: false,
    container: ref as React.RefObject<HTMLDivElement>,
    target: ref as React.RefObject<HTMLDivElement>,
    offset: ["start start", "end end"]
  });

  useMotionValueEvent(scrollY, "change", (latest) => {
    const scroll = getScrollPercentage(latest);
    // console.log("Scroll: ", latest, " Percent: ", scroll);
    progress.set(scroll);
    const animatedPortion = 1 - DEFAULT_THRESHOLD; // offset next step by animation length
    const fullStep = scroll / (1 / DEFAULT_STEP_COUNT); // out exact "step" point
    const newIdx = Math.min(
      DEFAULT_STEP_COUNT - 1,
      Math.max(0, Math.floor(fullStep + animatedPortion))
    );
    setCardIndex(newIdx);

    if (confettiRef && confettiRef.current) {
      if (newIdx === DEFAULT_STEP_COUNT - 1 && scroll > 0.83) {
        window.setTimeout(
          () =>
            confettiRef && confettiRef.current && confettiRef.current.play(),
          50
        );
        setTriggerConfetti(false);
        confettiRef.current.setSpeed(0.75);
      } else if (newIdx < DEFAULT_STEP_COUNT - 2 && !triggerConfetti) {
        confettiRef.current.goToAndStop(0);
        setTriggerConfetti(true);
      }
    }
  });

  const x = useTransform(
    progress,
    getCardScrollThresholds().map((step) => step.value),
    getCardScrollThresholds().map((step) => step.transform)
  );
  const waveY = useTransform(
    progress,
    getCircleScrollThresholds().map((step) => step.value),
    getCircleScrollThresholds().map((step) => step.transform.y)
  );
  const waveX = useTransform(
    progress,
    getCircleScrollThresholds().map((step) => step.value),
    getCircleScrollThresholds().map((step) => step.transform.x)
  );
  const waveScale = useTransform(
    progress,
    getCircleScrollThresholds().map((step) => step.value),
    getCircleScrollThresholds().map((step) => step.transform.scale)
  );
  const waveBackground = useTransform(
    progress,
    getCircleScrollThresholds().map((step) => step.value),
    getCircleScrollThresholds().map((step) => step.transform.background)
  );

  const overviewOpacity = useTransform(progress, [0, 0.003, 0.025], [1, 1, 0]);

  const y = useTransform(progress, [0, 1.05, 1.25], ["0%", "0%", "-100vh"]);

  const indexClass = (idx: number) =>
    cardIndex === idx
      ? "ss-stepActive"
      : cardIndex > idx
      ? "ss-stepPast"
      : "ss-stepFuture";

  return (
    <>
      <div id="ing-stickyStart" />
      <ContentPadding className="ing">
        <motion.div style={{ y }} className="ing-text">
          <div className="ing-featured">
            <Text p noPadding>
              Featured Project
            </Text>
          </div>
          <Text h3 noPadding>
            Ingram Content Group
          </Text>
          <Text p opacity={0.5} noPadding>
            Rethinking a Fortune 500 company's customer onboarding process:
          </Text>
        </motion.div>
      </ContentPadding>
      <motion.div
        style={{ y }}
        className="ing-sticky ing-stickyDesktop"
        id="ing-sticky"
      >
        <ContentPadding className="ing-overview ing-overviewDesktop">
          <Text h6 opacity={0.85} className="ing-problem">
            The Problem
          </Text>
          <Text p opacity={0.5} noPadding className="ing-problemP">
            Onboarding is too complicated. Staff manually intervene, info falls
            out of sync between departments. With tens of millions in yearly
            orders, Ingram needs an organized, streamlined process.
          </Text>
          <br />
          <br />
          <Text h6 opacity={0.85} className="ing-solution">
            The Solution
          </Text>
          <Text p opacity={0.5} noPadding className="ing-solutionP">
            Simplified onboarding for customers, and an automated submission
            process to keep Ingram's Microsoft cloud in sync.
          </Text>
          <Text p className="ss-see">
            See how we did it <span className="ss-seeRight">👉</span>
            <span className="ss-seeDown">👇</span>
          </Text>
        </ContentPadding>

        <motion.div
          className="ing-mobileUp"
          style={{ opacity: overviewOpacity }}
        >
          <ContentPadding className="ing-overview ing-overviewMobile">
            <Text h6 opacity={0.85} className="ing-problem">
              The Problem
            </Text>
            <Text p opacity={0.5} noPadding className="ing-problemP">
              Onboarding is too complicated. Staff manually intervene, info
              falls out of sync between departments. With tens of millions in
              yearly orders, Ingram needs an organized, streamlined process.
            </Text>
            <br />
            <br />
            <Text h6 opacity={0.85} className="ing-solution">
              The Solution
            </Text>
            <Text p opacity={0.5} noPadding className="ing-solutionP">
              Simplified onboarding for customers, and an automated submission
              process to keep Ingram's Microsoft cloud in sync.
            </Text>
            <Text p className="ss-see">
              See how we did it <span className="ss-seeRight">👉</span>
              <span className="ss-seeDown">👇</span>
            </Text>
          </ContentPadding>
        </motion.div>

        <div className="ing-stepsPadding">
          <motion.div className="ing-steps" style={{ x }}>
            <div className={`ss-step ${indexClass(0)}`}>
              <motion.div
                className="ss-circle"
                style={{
                  y: cardIndex === 0 ? waveY : 0,
                  x: cardIndex === 0 ? waveX : 0,
                  backgroundColor: waveBackground,
                  scale: waveScale
                }}
              >
                <Text h4 noPadding className="ss-stepCircleNum">
                  1
                </Text>
              </motion.div>
              <div className="ss-stepCard">
                <div className="ss-stepIcons">
                  <img
                    src={Jotform}
                    alt="Jotform Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img src={SASS} alt="SASS Icon" className="ss-stepIcon" />
                  <img
                    src={VSCode}
                    alt="VS Code Icon"
                    className="ss-stepIcon"
                  />
                </div>
                <Text h4 noPadding className="ss-stepNum">
                  New Order
                </Text>
                <Text p opacity={0.5} noPadding className="ss-stepText">
                  Customers submit orders through a JotForm. Custom UX
                  components and advanced logic eliminate additional steps.
                </Text>
              </div>
            </div>
            <div className={`ss-step ${indexClass(1)}`}>
              <motion.div
                className="ss-circle"
                style={{
                  y: cardIndex === 1 ? waveY : 0,
                  x: cardIndex === 1 ? waveX : 0,
                  backgroundColor: waveBackground,
                  scale: waveScale
                }}
              >
                <Text h4 noPadding className="ss-stepCircleNum">
                  2
                </Text>
              </motion.div>
              <div className="ss-stepCard">
                <div className="ss-stepIcons">
                  <img
                    src={Google_Drive}
                    alt="Google Drive Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Zapier}
                    alt="Zapier Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Azure}
                    alt="Azure Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Azure_Functions}
                    alt="Azure Functions Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                </div>
                <Text h4 noPadding className="ss-stepNum">
                  Run Automations
                </Text>
                <Text p opacity={0.5} noPadding className="ss-stepText">
                  Zapier pulls form submissions from Google Drive and sends the
                  data to Azure Functions for processing.
                </Text>
              </div>
            </div>
            <div className={`ss-step ${indexClass(2)}`}>
              <motion.div
                className="ss-circle"
                style={{
                  y: cardIndex === 2 ? waveY : 0,
                  x: cardIndex === 2 ? waveX : 0,
                  backgroundColor: waveBackground,
                  scale: waveScale
                }}
              >
                <Text h4 noPadding className="ss-stepCircleNum">
                  3
                </Text>
              </motion.div>
              <div className="ss-stepCard">
                <div className="ss-stepIcons">
                  <img
                    src={Zapier}
                    alt="Zapier Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Microsoft_Excel}
                    alt="Microsoft Excel Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Microsoft_Word}
                    alt="Microsoft Word Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={PDF}
                    alt="PDF Document Logo Icon"
                    className="ss-stepIcon"
                  />
                </div>
                <Text h4 noPadding className="ss-stepNum">
                  Create Documents
                </Text>
                <Text p opacity={0.5} noPadding className="ss-stepText">
                  A Zapier workflow receives processed data from Azure via
                  webhook, then builds several essential order documents for
                  staff.
                </Text>
              </div>
            </div>
            <div className={`ss-step ${indexClass(3)}`}>
              <motion.div
                className="ss-circle"
                style={{
                  y: cardIndex === 3 ? waveY : 0,
                  x: cardIndex === 3 ? waveX : 0,
                  backgroundColor: waveBackground,
                  scale: waveScale
                }}
              >
                <Text h4 noPadding className="ss-stepCircleNum">
                  4
                </Text>
              </motion.div>
              <div className="ss-stepCard">
                <div className="ss-stepIcons">
                  <img
                    src={Zapier}
                    alt="Zapier Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={OneDrive}
                    alt="OneDrive Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Power}
                    alt="Microsoft Power Automate Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Sharepoint}
                    alt="Microsoft SharePoint Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                </div>
                <Text h4 noPadding className="ss-stepNum">
                  Cloud Upload
                </Text>
                <Text p opacity={0.5} noPadding className="ss-stepText">
                  Upload documents to Ingram's Microsoft Cloud OneDrive,
                  triggering PowerAutomate to create a new SharePoint case file.
                </Text>
              </div>
            </div>
            <div className={`ss-step ${indexClass(4)}`}>
              <motion.div
                className="ss-circle"
                style={{
                  y: cardIndex === 4 ? waveY : 0,
                  x: cardIndex === 4 ? waveX : 0,
                  backgroundColor: waveBackground,
                  scale: waveScale
                }}
              >
                <Text h4 noPadding className="ss-stepCircleNum">
                  5
                </Text>
              </motion.div>
              <div className="ss-stepCard">
                <div className="ss-stepIcons">
                  <img
                    src={Power}
                    alt="Microsoft Power Automate Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Sharepoint}
                    alt="Microsoft SharePoint Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Microsoft_Teams}
                    alt="Integration Software Logo Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Outlook}
                    alt="Microsoft Outlook Software Logo Icon"
                    className="ss-stepIcon"
                  />
                </div>
                <Text h4 noPadding className="ss-stepNum">
                  Assign & Notify
                </Text>
                <Text p opacity={0.5} noPadding className="ss-stepText">
                  Case files are automatically assigned to the team, who receive
                  notifications for new and updated submissions.
                </Text>
              </div>
            </div>
            <div className={`ss-step ss-finalStep ${indexClass(5)}`}>
              <Lottie
                lottieRef={confettiRef}
                animationData={Confetti}
                autoplay={false}
                loop={1}
                className="ss-confetti"
              />
              <motion.div
                className="ss-circle"
                style={{
                  y: cardIndex === 5 ? waveY : 0,
                  x: cardIndex === 5 ? waveX : 0,
                  backgroundColor: waveBackground,
                  scale: waveScale
                }}
              >
                <Text h4 noPadding className="ss-stepCircleNum">
                  6
                </Text>
              </motion.div>
              <div className="ss-stepCard">
                <div className="ss-stepIcons">
                  <img
                    src={PartyFace}
                    alt="PartyFace Emoji Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Bottle}
                    alt="Bottle Emoji Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Party}
                    alt="Party Emoji Icon"
                    className="ss-stepIcon"
                  />
                  <img
                    src={Sparkles}
                    alt="Sparkles Emoji Icon"
                    className="ss-stepIcon"
                  />
                </div>
                <Text h4 noPadding className="ss-stepNum">
                  Celebrate
                </Text>
                <Text p opacity={0.5} noPadding className="ss-stepText">
                  A robust automated process keeps an organization running
                  smoothly, with everything in sync.
                </Text>
                <Text p opacity={1} className="ss-stepText">
                  Welcome to the future.
                </Text>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
      <div className="ing-spacer" id="ing-spacer" />
    </>
  );
});

export default Ingram;
